import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import {
  HomeIcon,
  AcademicCapIcon,
  UserGroupIcon,
  DeviceMobileIcon,
  CreditCardIcon,
  TagIcon,
  ChatIcon,
  KeyIcon,
  CogIcon,
  ChevronDownIcon,
  BookOpenIcon,
  StarIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/outline';
import { useSidebar } from '../../context/SidebarContext';
import LogoLight from '../../assets/images/logo-light.png';
import LogoDark from '../../assets/images/logo-dark.png';
import { selectPermissions } from '../../store/slices/permissionsSlice';
import { hasPermission } from '../../services/permissionsService';

const menuItems = [
  {
    title: 'لوحة التحكم',
    icon: HomeIcon,
    path: '/admin/dashboard',
    permission: 'access_dashboard'
  },
  {
    title: 'الدورات',
    icon: BookOpenIcon,
    permission: 'courses_access',
    submenu: [
      { title: 'إنشاء', path: '/admin/dashboard/courses/create', permission: 'create_course' },
      { title: 'الدورات', path: '/admin/dashboard/courses', permission: 'view_courses' },
      { title: 'محتوى الدورات', path: '/admin/dashboard/courses/content', permission: 'view_course_content' },
      { title: 'الاختبارات', path: '/admin/dashboard/courses/exams', permission: 'view_exams' },
      { title: 'التصنيفات', path: '/admin/dashboard/courses/categories', permission: 'view_categories' },
      { title: 'إدارة المشتركين', path: '/admin/dashboard/courses/enrollments', permission: 'view_financial_statistics' }
    ]
  },
  {
    title: 'المستخدمين',
    icon: UserGroupIcon,
    permission: 'users_access',
    submenu: [
      { title: 'المشرفين', path: '/admin/dashboard/users/admins', permission: 'view_admins' },
      { title: 'المدربين', path: '/admin/dashboard/users/instructor', permission: 'view_instructors' },
      { title: 'المتدربين', path: '/admin/dashboard/users/trainees', permission: 'view_trainees' }
    ]
  },
  {
    title: 'الأجهزة',
    icon: DeviceMobileIcon,
    permission: 'devices_access',
    submenu: [
      { title: 'المستخدمين', path: '/admin/dashboard/devices/users', permission: 'view_devices' },
      { title: 'أجهزة المستخدمين', path: '/admin/dashboard/devices/user-devices', permission: 'manage_devices' }
    ]
  },
  {
    title: 'المدفوعات',
    icon: CreditCardIcon,
    permission: 'payments_access',
    submenu: [
      { title: 'التحويل البنكي', path: '/admin/dashboard/payments/bank-transfers', permission: 'view_bank_transfers' },
      { title: 'الدفع الإلكتروني', path: '/admin/dashboard/payment/electronic', permission: 'view_electronic_payments' }
    ]
  },
  {
    title: 'الخصومات',
    icon: TagIcon,
    permission: 'discounts_access',
    submenu: [
      { title: 'كوبونات الخصم', path: '/admin/dashboard/discounts/coupons', permission: 'view_coupons' },
      { title: 'خصومات الدورة', path: '/admin/dashboard/discounts/courses', permission: 'view_course_discounts' }
    ]
  },
  {
    title: 'الكتب',
    icon: BookOpenIcon,
    permission: 'books_access',
    submenu: [
      { title: 'عرض الكتب', path: '/admin/dashboard/books', permission: 'view_books' },
      { title: 'إضافة كتاب', path: '/admin/dashboard/books/create', permission: 'create_book' },
      { title: 'الطلبات', path: '/admin/dashboard/books/orders', permission: 'view_book_orders' }
    ]
  },
  {
    title: 'التقييمات والآراء',
    icon: StarIcon,
    permission: 'view_reviews',
    submenu: [
      { 
        title: 'تقييمات العملاء', 
        path: '/admin/dashboard/reviews', 
        permission: 'view_reviews' 
      },
      { 
        title: 'تجارب المشتركين', 
        path: '/admin/dashboard/experiences', 
        permission: 'view_reviews' 
      }
    ]
  },
  {
    title: 'اسأل مدرب',
    icon: QuestionMarkCircleIcon,
    path: '/admin/dashboard/ask-instructor',
    permission: 'view_instructor_questions'
  },
  {
    title: 'الإعدادات',
    icon: CogIcon,
    path: '/admin/dashboard/settings',
    permission: 'view_settings' // تم تصحيح اسم الصلاحية ليتوافق مع SecuritySettings.jsx
  }
];

const MenuItem = ({ item, isOpen, toggleSubmenu, isCollapsed }) => {
  const Icon = item.icon;
  
  if (item.submenu) {
    return (
      <div className="mb-2">
        <div
          className="flex items-center py-3 text-gray-600 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer relative"
          onClick={() => toggleSubmenu()}
        >
          <div className="absolute right-[3px] w-16 flex justify-start">
            <div className="w-5 h-5">
              {Icon && <Icon className="w-full h-full" />}
            </div>
          </div>
          <div className={`overflow-hidden ${isCollapsed ? 'w-0' : 'w-full pr-6'}`}>
            <div className="transition-all duration-300 flex items-center">
              <span className="flex-1 whitespace-nowrap pr-4">{item.title}</span>
              <ChevronDownIcon
                className={`w-4 h-4 transition-transform duration-300 ${
                  isOpen ? 'transform rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </div>
        
        {!isCollapsed && (
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ overflow: "hidden" }}
                className="mr-8 mt-2 space-y-2"
              >
                {item.submenu.map((subItem, index) => (
                  <div key={index}>
                    {subItem.submenu ? (
                      <MenuItem
                        item={subItem}
                        isOpen={false}
                        toggleSubmenu={() => {}}
                        isCollapsed={isCollapsed}
                      />
                    ) : (
                      <Link
                        to={subItem.path}
                        className="flex items-center py-2 text-sm text-gray-600 dark:text-white hover:text-gray-600 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg relative"
                      >
                        <div className="absolute right-[3px] w-16 flex justify-start">
                          <div className="w-1.5 h-1.5 bg-gray-300 dark:bg-gray-500 rounded-full"></div>
                        </div>
                        <div className={`overflow-hidden ${isCollapsed ? 'w-0' : 'w-full pr-6'}`}>
                          <div className="transition-all duration-300 flex items-center">
                            <span className="whitespace-nowrap pr-4">{subItem.title}</span>
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  }

  // للعناصر التي ليس لديها قائمة فرعية
  return (
    <Link
      to={item.path}
      className="flex items-center py-3 text-gray-600 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer relative mb-2"
    >
      <div className="absolute right-[3px] w-16 flex justify-start">
        <div className="w-5 h-5">
          {Icon && <Icon className="w-full h-full" />}
        </div>
      </div>
      <div className={`overflow-hidden ${isCollapsed ? 'w-0' : 'w-full pr-6'}`}>
        <div className="transition-all duration-300 flex items-center">
          <span className="flex-1 whitespace-nowrap pr-4">{item.title}</span>
        </div>
      </div>
    </Link>
  );
};

function Sidebar() {
  const [openMenu, setOpenMenu] = useState(null);
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));
  // جلب الصلاحيات من Redux
  const permissions = useSelector(selectPermissions);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDarkMode(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    setIsDarkMode(document.documentElement.classList.contains('dark'));

    return () => observer.disconnect();
  }, []);

  // التحقق من صلاحيات العنصر
  const hasItemPermission = (item) => {
    // إذا لم يكن هناك صلاحية مطلوبة، نعتبر أنه مسموح
    if (!item.permission) return true;
    return hasPermission(item.permission);
  };

  // التحقق مما إذا كان العنصر له أي عناصر فرعية مرئية
  const hasVisibleSubmenu = (item) => {
    if (!item.submenu) return false;
    return item.submenu.some(subItem => hasItemPermission(subItem));
  };
  
  const toggleSubmenu = (index) => {
    setOpenMenu(openMenu === index ? null : index);
  };

  return (
    <div className={`fixed right-0 top-0 h-screen bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'} overflow-hidden`}>
      <div className="h-16 px-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <div className={`w-[240px] flex items-center justify-between`}>
          <div className={`transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>
            <img 
              src={isDarkMode ? LogoDark : LogoLight}
              alt="Logo" 
              className="h-10 w-auto transition-opacity duration-300" 
            />
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {isCollapsed ? (
              <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-white" />
            ) : (
              <ArrowRightIcon className="w-5 h-5 text-gray-600 dark:text-white" />
            )}
          </button>
        </div>
      </div>
      
      <div className="p-4 space-y-0.5 overflow-y-scroll scrollbar-hide h-[calc(100vh-4rem)]">
        {menuItems
          // تصفية العناصر بناءً على الصلاحيات
          .filter(item => {
            // إذا كان العنصر له قائمة فرعية، نتحقق من وجود عناصر فرعية مرئية
            if (item.submenu) {
              return hasVisibleSubmenu(item);
            }
            // غير ذلك، تحقق من صلاحية العنصر نفسه
            return hasItemPermission(item);
          })
          .map((item, index) => (
            <MenuItem
              key={index}
              item={{
                ...item,
                // تصفية العناصر الفرعية إذا كانت موجودة
                submenu: item.submenu ? item.submenu.filter(subItem => hasItemPermission(subItem)) : undefined
              }}
              isOpen={openMenu === index}
              toggleSubmenu={() => toggleSubmenu(index)}
              isCollapsed={isCollapsed}
            />
          ))}
      </div>
    </div>
  );
}

export default Sidebar;
